import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Corners from "./Corners";

const STATS = [
  { value: 221, suffix: "", label: "LEADS GENERATED" },
  { value: 10, suffix: "", label: "DAYS" },
  { value: 800, prefix: "$", label: "AD SPEND" },
  { value: 3.65, prefix: "$", label: "COST PER LEAD", decimals: 2 },
];

const TESTIMONIALS = [
  {
    name: "MARCUS HAYES",
    role: "MLO · FHA + FIRST-TIME BUYER",
    quote:
      "First webinar produced 34 registrants in a week. Two of them closed in 60 days. My CRM finally does the follow-up for me.",
  },
  {
    name: "PRIYA SHAH",
    role: "BROKER OWNER · DSCR INVESTOR NICHE",
    quote:
      "I stopped chasing realtors. Now investors book applications directly from the replay page. Cleanest pipeline of my career.",
  },
  {
    name: "JORDAN LEE",
    role: "LOAN OFFICER · VA / MILITARY BASES",
    quote:
      "The webinar script + niche framing changed everything. My close rate on booked calls doubled because they show up educated.",
  },
];

function CountUp({ target, prefix = "", suffix = "", decimals = 0, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start;
    const dur = 1800;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(target * eased);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target]);
  const formatted = decimals ? n.toFixed(decimals) : Math.round(n).toLocaleString();
  return (
    <span className="tabular-mono">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

export default function Proof() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section
      id="proof"
      className="relative py-32 md:py-48"
      data-testid="proof-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#F0ECE4]/40">
            [004] —
          </span>
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]">
            THE PROOF
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display max-w-[1100px] mb-16"
          style={{ fontSize: "clamp(34px, 5vw, 80px)", lineHeight: 0.95, letterSpacing: "-0.035em" }}
        >
          What happens when an MLO <span className="text-[#C9A84C]">owns their traffic.</span>
        </motion.h2>

        {/* Case Study Card */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative border border-[#C9A84C]/30 bg-white/[0.025] p-8 md:p-16"
          data-testid="case-study-card"
        >
          <Corners />
          {/* Chapter mark */}
          <div className="absolute -top-3 left-8 bg-[#070709] px-3 font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]">
            CASE STUDY / 001
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1.4fr] gap-10 md:gap-14 items-start">
            {/* Left — client */}
            <div>
              <div className="font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/50 mb-4">
                CLIENT PROFILE
              </div>
              <div className="font-display text-[28px] md:text-[36px] leading-[0.95] tracking-[-0.03em]">
                CASSANDRA DORSAINVIL
              </div>
              <div className="mt-3 font-mono text-[11px] tracking-[0.14em] text-[#F0ECE4]/55 leading-[1.7]">
                20-YEAR MLO VETERAN<br />
                #11 OF 3,300 LOs AT NEXA MORTGAGE
              </div>
              <div className="mt-6 inline-block border border-[#C9A84C]/40 px-3 py-1 font-mono text-[10px] tracking-[0.24em] text-[#C9A84C]">
                NICHE · VA LOANS
              </div>
            </div>

            {/* Divider */}
            <div className="hidden md:block h-full w-px bg-[#C9A84C]/25 min-h-[300px]" />

            {/* Right — stats */}
            <div>
              <div className="font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/50 mb-6">
                RESULTS · FIRST WEBINAR LAUNCH
              </div>
              <div className="grid grid-cols-2 gap-8 md:gap-10">
                {STATS.map((s, i) => (
                  <div key={i} data-testid={`stat-${i}`}>
                    <div
                      className="font-display text-[#C9A84C]"
                      style={{ fontSize: "clamp(38px, 4vw, 60px)", lineHeight: 1, letterSpacing: "-0.04em" }}
                    >
                      <CountUp
                        target={s.value}
                        prefix={s.prefix}
                        suffix={s.suffix}
                        decimals={s.decimals || 0}
                        active={inView}
                      />
                    </div>
                    <div className="mt-2 font-mono text-[10px] tracking-[0.24em] text-[#F0ECE4]/55">
                      {s.label}
                    </div>
                  </div>
                ))}
                <div>
                  <div
                    className="font-display text-[#F0ECE4]"
                    style={{ fontSize: "clamp(28px, 3vw, 44px)", lineHeight: 1, letterSpacing: "-0.03em" }}
                  >
                    DOZENS
                  </div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.24em] text-[#F0ECE4]/55">
                    PRE-APPROVAL APPLICATIONS
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quote */}
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-14 border-t wam-hairline pt-10 max-w-[900px]"
          >
            <div className="font-display text-[42px] leading-none text-[#C9A84C]">&ldquo;</div>
            <p className="mt-2 font-body italic text-[18px] md:text-[20px] leading-[1.55] text-[#F0ECE4]/90">
              I stopped doing 30-minute consultations with people who weren&rsquo;t ready.
              Now every conversation starts with someone who already knows the process
              and has decided to move forward.
            </p>
            <div className="mt-6 font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]">
              — CASSANDRA DORSAINVIL
            </div>
          </motion.blockquote>
        </motion.div>

        {/* Testimonial Row */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -4 }}
              className="wam-card relative"
              data-testid={`testimonial-${i}`}
            >
              <Corners />
              <div className="font-display text-[30px] leading-none text-[#C9A84C]">&ldquo;</div>
              <p className="mt-3 font-body text-[14px] leading-[1.7] text-[#F0ECE4]/80">
                {t.quote}
              </p>
              <div className="mt-6 border-t wam-hairline pt-4">
                <div className="font-display text-[15px] tracking-[-0.02em]">
                  {t.name}
                </div>
                <div className="mt-1 font-mono text-[9px] tracking-[0.24em] text-[#F0ECE4]/50">
                  {t.role}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
